import { useEffect } from "react";
import { ArrowUpCircle, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import type { GatedCapability } from "@/features/billing/hooks/usePlanAccess";
import type { BillingGateReason } from "@/types/api";
import {
  PLAN_GATE_SUBSCRIBE_TITLE,
  PLAN_GATE_UPGRADE_TITLE,
  PLAN_LINK_VIEW,
  PLAN_LINK_VIEW_PRO,
} from "@/features/billing/planCopy";

interface BillingGateModalProps {
  open: boolean;
  reason: BillingGateReason | null;
  /** Reserved for analytics or future copy; body stays plan-level, not feature-specific. */
  capability?: GatedCapability;
  onClose: () => void;
}

export function BillingGateModal({
  open,
  reason,
  onClose,
}: BillingGateModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || !reason) return null;

  const isUpgrade = reason === "upgrade_to_pro_required";
  const title = isUpgrade ? PLAN_GATE_UPGRADE_TITLE : PLAN_GATE_SUBSCRIBE_TITLE;
  const Icon = isUpgrade ? ArrowUpCircle : CreditCard;

  return (
    <div
      className="billing-gate-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="billing-gate-title"
      onClick={onClose}
    >
      <div
        className="billing-gate-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="billing-gate-icon" aria-hidden>
          <Icon size={20} strokeWidth={2.25} />
        </div>
        <h2 id="billing-gate-title" className="billing-gate-title">
          {title}
        </h2>
        <div className="billing-gate-actions">
          <button
            type="button"
            className="billing-gate-btn billing-gate-btn--ghost"
            onClick={onClose}
          >
            Not now
          </button>
          <Link
            to={ROUTES.billing}
            className="billing-gate-btn billing-gate-btn--primary"
            onClick={onClose}
          >
            {isUpgrade ? PLAN_LINK_VIEW_PRO : PLAN_LINK_VIEW}
          </Link>
        </div>
      </div>
    </div>
  );
}
